/**
 * A screen that shows a spinner while a write runs, then goes back to the page
 * that opened it, such as "Recording vote..." after "Skip and submit vote".
 *
 * The write is held in memory under an id, and the screen's URL carries only
 * that id, so opening the URL on its own writes nothing: the screen finds no
 * write and goes straight back.
 */

import { useTheme } from '@repo/theme';
import { Pad, PadBox } from 'component/basics';
import { UtilityText } from 'component/text';
import { useEffect, useRef, useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { showApiErrorToast } from 'system/orpc-errors';
import { type Datastore, useDatastore } from 'util/datastore';
import { fireAndForget } from 'util/hooks';
import { canGoBack } from 'util/navigate';
import { useScreenParams } from 'util/params';
import type { TLabel } from 'util/stdtypes';
import { generateRandomKey } from 'util/util';

const MIN_DISPLAY_MS = 1000;

type Recording = { label: TLabel; work: () => Promise<unknown> };

const recordings = new Map<string, Recording>();

/** Open the recording screen over the current page while `work` runs. */
export function pushRecordingScreen({ datastore, label, work }: { datastore: Datastore; label: TLabel } & Pick<Recording, 'work'>) {
    const recordingId = generateRandomKey(12);
    recordings.set(recordingId, { label, work });
    datastore.pushSubscreen('recording', { recordingId });
}

export function RecordingScreen() {
    const datastore = useDatastore();
    const { colors } = useTheme();
    const { recordingId } = useScreenParams();
    const [recording] = useState(() => takeRecording(recordingId));
    const startedRef = useRef(false);

    useEffect(() => {
        if (startedRef.current) return;
        startedRef.current = true;
        if (!recording) {
            leave(datastore);
            return;
        }
        const shownLongEnough = new Promise((resolve) => setTimeout(resolve, MIN_DISPLAY_MS));
        const done = recording.work().catch((error: unknown) => showApiErrorToast({ datastore, error }));
        fireAndForget(Promise.all([done, shownLongEnough]).then(() => leave(datastore)));
    }, [datastore, recording]);

    return (
        <View style={s.container}>
            <PadBox horiz={20} vert={20}>
                <ActivityIndicator color={colors.colorButtonAccent} size='large' />
                <Pad size={16} />
                {recording && <UtilityText label={recording.label} weight='strong' />}
            </PadBox>
        </View>
    );
}

function takeRecording(recordingId: string | undefined): Recording | undefined {
    const recording = recordingId ? recordings.get(recordingId) : undefined;
    if (recordingId) recordings.delete(recordingId);
    return recording;
}

function leave(datastore: Datastore) {
    if (canGoBack()) {
        datastore.goBack();
    } else {
        datastore.gotoInstance({ structureKey: datastore.getStructureKey(), instanceKey: datastore.getInstanceKey(), replace: true });
    }
}

const s = StyleSheet.create({
    container: {
        flexGrow: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
});
